import { getData, handleErrors, postData } from "./common.service";

export enum InventoryLocationPartnerType {
  DISTRIBUTOR = "DISTRIBUTOR",
  RETAILER = "RETAILER",
}

export async function postInventoryLocation(payload: any) {
  return postData("/inventory-locations", {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: payload,
  }).then(handleErrors);
}

export async function patchInventoryLocation(id: string, payload: any) {
  return postData(`/inventory-locations/${id}`, {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    usePatchMethod: true,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: payload,
  }).then(handleErrors);
}

// ChannelPartnerList.tsx's page size -- small on purpose (see MY_INVENTORY_LOCATIONS_LIMIT
// below for why loading everything up front was dropped in favor of this +
// infinite scroll + server-side search).
export const CHANNEL_PARTNERS_PAGE_SIZE = 50;

// MoveStockForm's From/To pickers still want "everything the caller can
// access" in one shot -- their picker UI (SelectItem's plain `items` mode)
// has no pagination, only its own client-side search over whatever's
// already loaded. No hard ceiling backs this beyond the query limit below
// -- MoveStockForm treats a result whose length equals this as possibly
// truncated (more may exist) rather than assuming it's the complete set.
export const MY_INVENTORY_LOCATIONS_LIMIT = 500;

// Deliberately sends no access-scoping where-clause of its own -- fyllo-core's
// TerritoryInventoryScopeInterceptor is bound to
// InventoryLocationController.find/count and ANDs its own scope condition
// (caller's home territoryId + additionalTerritoryIds, resolved fresh from
// the FarmUserRepository on every request, plus the DISTRIBUTOR
// read-exemption) into whatever `where` is sent here, no matter what. A
// client-computed territory filter would only ever narrow that further,
// never correct it -- and since this app caches the fetched profile in
// localStorage indefinitely (see App.tsx), a client-side filter can go
// stale and silently under-return results the backend would otherwise
// serve correctly.
//
// `search` matches businessName OR name (case-insensitive substring) --
// mirrors the `loc.businessName ?? loc.name` fallback ChannelPartnerList.tsx
// used when this filtering was still client-side. Runs server-side now
// specifically so a match beyond the first page is still findable without
// loading everything first (the entire point of paginating this).
export async function getMyInventoryLocations(options: {
  partnerType?: InventoryLocationPartnerType;
  search?: string;
  skip?: number;
  limit?: number;
} = {}) {
  const { partnerType, search, skip = 0, limit = MY_INVENTORY_LOCATIONS_LIMIT } = options;
  const conditions: any[] = [];
  if (partnerType) conditions.push({ partnerType });
  const text = search?.trim();
  if (text) {
    conditions.push({
      or: [
        { businessName: { like: `.*${text}.*`, options: "i" } },
        { name: { like: `.*${text}.*`, options: "i" } },
      ],
    });
  }
  const qs: any = { limit, skip };
  if (conditions.length === 1) {
    qs.where = conditions[0];
  } else if (conditions.length > 1) {
    qs.where = { and: conditions };
  }
  return getData("/inventory-locations?filter=" + JSON.stringify(qs), {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then((res) => res.json());
}

// Deliberately unscoped by salesPersonId -- distributors are visible to any
// support user org-wide (the backend's read-side exemption), so a retailer
// can be linked under a distributor its creator doesn't personally manage.
export async function getDistributors() {
  const qs = {
    where: { partnerType: InventoryLocationPartnerType.DISTRIBUTOR },
    limit: 200,
  };
  return getData("/inventory-locations?filter=" + JSON.stringify(qs), {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then((res) => res.json());
}

export async function getInventoryLocationByIdFull(id: string) {
  return getData(`/inventory-locations/${id}`, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then((res) => res.json());
}

export async function postInventoryOrder(payload: {
  date: string;
  quantity: number;
  inventoryLocationId: string;
  inventoryItemId: string;
  comments?: string;
}) {
  return postData("/inventory-orders", {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: payload,
  }).then(handleErrors);
}

// Shared page size for the Orders tab's history feeds -- matches how
// OrderList.tsx detects "no more pages" (items.length < this ⇒ done).
export const INVENTORY_HISTORY_PAGE_SIZE = 20;

// Scoped server-side by SalesPersonOrderScopeInterceptor -- a support user
// only ever gets back orders for locations they're the assigned salesperson
// for, regardless of the inventoryLocationId passed in here.
export async function getInventoryOrdersForLocation(inventoryLocationId: string, skip = 0) {
  const qs = {
    where: { inventoryLocationId },
    order: "date DESC",
    include: ["inventoryItem"],
    limit: INVENTORY_HISTORY_PAGE_SIZE,
    skip,
  };
  return getData("/inventory-orders?filter=" + JSON.stringify(qs), {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then((res) => res.json());
}

// A movement's location can be on either side (from or to) -- scoped
// server-side by SalesPersonMovementScopeInterceptor's find handling
// (OR of both sides against the caller's own assigned locations).
//
// Deliberately NOT filtered to type: INTERNAL_TRANSFER -- this same model
// also carries device installs/returns at farmer plots (INSTALLATION,
// RETURNED_TO_INVENTORY), production consumption, order fulfillment, etc.,
// and those are real history for this location too. Those types only ever
// populate one side (fromInventoryLocationId OR toInventoryLocationId, not
// both, since they're not location-to-location transfers) -- the UI layer
// (LocationOrders.tsx) renders each type appropriately rather than forcing
// everything into "Sent to/Received from" framing, which only makes sense
// for INTERNAL_TRANSFER.
export async function getInventoryMovementsForLocation(locationId: string, skip = 0) {
  const qs = {
    where: { or: [{ fromInventoryLocationId: locationId }, { toInventoryLocationId: locationId }] },
    order: "date DESC",
    include: ["inventoryItem", "fromInventoryLocation", "toInventoryLocation"],
    limit: INVENTORY_HISTORY_PAGE_SIZE,
    skip,
  };
  return getData("/inventory-movements?filter=" + JSON.stringify(qs), {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then((res) => res.json());
}

// Item catalog is org-wide, not location-scoped -- no salesPersonId
// filtering applies (or is needed) here.
export async function getInventoryItemsCatalog() {
  const qs = { fields: ["id", "name"], limit: 500 };
  return getData("/inventory-items?filter=" + JSON.stringify(qs), {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then((res) => res.json());
}

// Scoped server-side by SalesPersonMovementScopeInterceptor -- a support
// user can only move stock between two locations they're the assigned
// salesperson for (checked on both sides). `type` defaults to
// INTERNAL_TRANSFER when omitted; ORDER_FULFILLMENT can be passed
// explicitly when this transfer is satisfying the To location's
// outstanding order (see MoveStockForm.tsx) so the backend decrements
// that location's on-order count. The backend re-validates and forces
// anything else back to INTERNAL_TRANSFER regardless of what's submitted.
export async function postInventoryMovement(payload: {
  date: string;
  quantity: number;
  fromInventoryLocationId: string;
  toInventoryLocationId: string;
  inventoryItemId: string;
  comments?: string;
  type?: "INTERNAL_TRANSFER" | "ORDER_FULFILLMENT";
}) {
  return postData("/inventory-movements", {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: { ...payload, type: payload.type ?? "INTERNAL_TRANSFER" },
  }).then(handleErrors);
}

// GST/shop-registration certificate -- not an image, so it goes through the
// (now-authenticated) generic /files/upload rather than the /images/... S3
// pattern image.service.ts's uploadFile() uses for the shop-front photo.
export async function uploadDocument(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  return postData("/files/upload", {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    headers: {
      Accept: "application/json",
    },
    body: formData,
  }).then(handleErrors);
}
